import { Injectable } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { VonPageResponseModel } from '@von-development-studio/angular-rest-service';
import { FilterMetadata, MenuItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { BaseService } from 'src/app/core/base/base.service';
import { consultPatientActions } from 'src/app/core/component/consult-patient/store/consult-patient.action';
import { RestService } from 'src/app/core/service/rest.service';
import { CommonPatientViewModel } from 'src/app/model/common/patient.view';
import { CommonTurnViewModel } from 'src/app/model/common/turn.view';
import { commonTurnActions } from 'src/app/store/common/turn/turn.action';
import { environment } from 'src/environments/environment';
import { TURN } from '../turn.constants';
import { TurnCommentDialogComponent } from './turn.comment-dialog.component';
import { TurnDialogComponent } from './turn.dialog.component';

@Injectable({
  providedIn: 'root',
})
export class TurnService extends BaseService {
  readonly screen = TURN;

  constructor(
    protected store: Store<any>,
    protected dialogService: DialogService,
    protected rest: RestService,
    protected router: Router
  ) {
    super();
  }

  getTurnStore = (): Observable<CommonTurnViewModel[]> =>
    this.store
      .select('common', 'turn', 'result')
      .pipe(tap(() => this.setLoadingState(false)));

  getOldTurnStore = (): Observable<VonPageResponseModel<CommonTurnViewModel>> =>
    this.store
      .select('common', 'turn', 'lazyLoad')
      .pipe(tap(() => this.setLoadingState(false)));

  readRecipeStore = (): Observable<{
    recipeFile?: SafeResourceUrl;
    showRecipePdf: boolean;
  }> => this.store.select('common', 'turn', 'file');

  cleanConsultPatientStore = () =>
    this.store.dispatch(consultPatientActions.setAllDefaultAction({}));

  openNewTurnDialog = () =>
    this.openDialog(
      {
        goToConsult: this.goToConsult,
      },
      this.screen.actions.add
    );

  openPatchDoctorDialog = (
    turnId: number,
    specialty: string,
    patientHelp: CommonPatientViewModel
  ) =>
    this.openDialog(
      { turnId, specialty, patientHelp, isPatch: true, isPatchDoctor: true },
      this.screen.actions.updateDoctor
    );

  openPatchStatusDialog = (
    turnId: number,
    specialtyCodeAlt: string,
    patientHelp: CommonPatientViewModel
  ) =>
    this.openDialog(
      {
        turnId,
        specialtyCodeAlt,
        patientHelp,
        isPatch: true,
        isPatchStatus: true,
      },
      this.screen.actions.updateStatus
    );

  openCommentDialog = (turn: CommonTurnViewModel) =>
    this.openDialog(
      turn,
      this.screen.actions.addComment,
      TurnCommentDialogComponent
    );

  readOldTurns = (
    page: number,
    size: number,
    patient?: FilterMetadata,
    doctor?: FilterMetadata,
    specialty?: FilterMetadata,
    doctorUsername?: string
  ): Observable<VonPageResponseModel<CommonTurnViewModel>> => {
    const body: any = { page, size };
    if (patient) {
      body.patient = patient.value;
    }
    if (doctor) {
      body.doctor = doctor.value;
    }
    if (specialty) {
      body.specialty = specialty.value;
    }

    return this.rest
      .get<VonPageResponseModel<CommonTurnViewModel>>({
        url: `${environment.endpoints.common}/turn/lazy`,
        params: body,
      })
      .pipe(
        tap((result) => {
          const turnsPaged: VonPageResponseModel<CommonTurnViewModel> = {
            ...result,
            content: result.content.map((item) => {
              const actions: MenuItem[] = [];

              if (item.doctorUsername === doctorUsername) {
                actions.push({
                  label: this.screen.actions.editConsult,
                  icon: 'fas fa-pencil-alt',
                  styleClass: 'text-warning',
                  command: () => this.goToConsult(item, false, true),
                });
              }

              if (item.hasRecipe) {
                actions.push({
                  label: this.screen.actions.seeRecipe,
                  icon: 'fas fa-file-signature',
                  styleClass: 'text-secondary',
                  command: () => {
                    this.setLoadingState();
                    this.generateRecipe(item.id!).subscribe((resource) =>
                      this.store.dispatch(
                        commonTurnActions.generatedRecipeAction({
                          recipeFile: resource,
                          showRecipePdf: true,
                        })
                      )
                    );
                  },
                });
              }

              return {
                ...item,
                actions,
              };
            }),
          };
          this.store.dispatch(
            commonTurnActions.setLazyLoadResultAction(turnsPaged)
          );
        })
      );
  };

  generateHistory = (
    historyNumber: string,
    turnId: number,
    specialty: string
  ): Observable<SafeResourceUrl> => {
    let historyRest = this.generateGeneralHistory;
    if (specialty.toLowerCase() === 'odon') {
      historyRest = this.generateOdontologyHistory;
    }
    return historyRest(historyNumber, turnId);
  };

  generateGeneralHistory = (
    historyNumber: string,
    turnId: number
  ): Observable<SafeResourceUrl> =>
    this.rest
      .get({
        url: `${environment.endpoints.report}/medical-history/{historyNumber}/{turnId}`,
        urlParams: { historyNumber, turnId },
        header: {
          responseType: 'BLOB' as 'json',
        },
      })
      .pipe(map(this.rest.mapResponseAsPdf));

  generateOdontologyHistory = (
    historyNumber: string,
    turnId: number
  ): Observable<SafeResourceUrl> =>
    this.rest
      .get({
        url: `${environment.endpoints.report}/odontology-history/{historyNumber}/{turnId}`,
        urlParams: { historyNumber, turnId },
        header: {
          responseType: 'BLOB' as 'json',
        },
      })
      .pipe(map(this.rest.mapResponseAsPdf));

  generateRecipe = (turnId: number): Observable<SafeResourceUrl> =>
    this.rest
      .get({
        url: `${environment.endpoints.report}/medical-recipe`,
        params: { turnId },
        header: {
          responseType: 'BLOB' as 'json',
        },
      })
      .pipe(map(this.rest.mapResponseAsPdf));

  goToConsult = (
    turn: CommonTurnViewModel,
    goToVital: boolean = false,
    skipLocationChange: boolean = false
  ) => {
    if (goToVital) {
      this.router.navigate(['/signos-vitales'], {
        queryParams: turn,
        skipLocationChange: true,
      });
    } else if (!goToVital && turn.specialtyCodeAlt?.toLowerCase() !== 'odon') {
      this.router.navigate(['/consulta'], {
        queryParams: { turn: turn.id },
        skipLocationChange,
      });
    } else if (!goToVital && turn.specialtyCodeAlt?.toLowerCase() === 'odon') {
      this.router.navigate(['/odontologia'], {
        queryParams: { turn: turn.id },
        skipLocationChange,
      });
    }
  };

  private openDialog = (
    data: any,
    header: string,
    component: any = TurnDialogComponent
  ) =>
    this.dialogService.open(component, {
      data,
      header,
      closeOnEscape: true,
      width: '60%',
      style: {
        maxWidth: '600px',
      },
    });
}
